import { useCallback, useRef, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { acquireTokenSafe } from '../App';
import { getApiBaseUrl } from '../utils/baseUrl';

export const useTelemetry = () => {
    const { instance, accounts } = useMsal();
    const pageStartTime = useRef<number>(Date.now());
    const currentPath = useRef<string>('');

    const getToken = async () => {
        return acquireTokenSafe(instance, accounts);
    };

    const getUserInfo = () => {
        if (accounts.length === 0) return { userId: '', userEmail: '', userName: '' };
        const account = accounts[0];
        return {
            userId: account.localAccountId || account.homeAccountId || '',
            userEmail: account.username || '',
            userName: account.name || ''
        };
    };

    const logLogin = useCallback(async () => {
        const token = await getToken();
        if (!token) return;
        
        const userInfo = getUserInfo();
        try {
            await fetch(`${getApiBaseUrl()}/api/telemetry/login`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(userInfo)
            });
        } catch (err) {
            console.error('Failed to log login:', err);
        }
    }, [instance, accounts]);

    const logLogout = useCallback(async () => {
        const token = await getToken();
        if (!token) return;
        
        const userInfo = getUserInfo();
        try {
            await fetch(`${getApiBaseUrl()}/api/telemetry/logout`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(userInfo)
            });
        } catch (err) {
            console.error('Failed to log logout:', err);
        }
    }, [instance, accounts]);

    const logPageView = useCallback(async (pagePath: string, pageName: string) => {
        const token = await getToken();
        if (!token) return;
        
        // Calculate duration since last page view
        const duration = Math.round((Date.now() - pageStartTime.current) / 1000);
        pageStartTime.current = Date.now();
        currentPath.current = pagePath;
        
        const userInfo = getUserInfo();
        try {
            await fetch(`${getApiBaseUrl()}/api/telemetry/pageview`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    ...userInfo,
                    pagePath,
                    pageName,
                    duration
                })
            });
        } catch (err) {
            console.error('Failed to log page view:', err);
        }
    }, [instance, accounts]);

    return { logLogin, logLogout, logPageView };
};

// Page name mappings
export const getPageName = (path: string): string => {
    const pageNames: Record<string, string> = {
        '/': 'Home',
        '/clients': 'Client Listing',
        '/client-type': 'Client Reports',
        '/marketing': 'Marketing',
        '/sales': 'Sales',
        '/people': 'People',
        '/delivery': 'Delivery',
        '/timekeeping': 'Timekeeping',
        '/partners': 'Partner Reports',
        '/expired-ids': 'Expired ID Reports',
        '/review-companies': 'Review Companies',
        '/cro-discrepancies': 'CRO Discrepancies',
        '/cro-data-dump': 'CRO Data Dump',
        '/client-tenure': 'Client Tenure',
        '/service-reports': 'Service Reports',
        '/work-activity': 'Work Activity',
        '/scheduled-jobs': 'Scheduled Jobs',
        '/metadata': 'Caching',
        '/draft-invoices': 'Draft Invoices',
        '/enhanced-timekeeping': 'Enhanced Timekeeping',
        '/client-linking': 'Client Linking',
        '/client-duplicates': 'Client Duplicates',
        '/permissions': 'Permissions',
        '/system-report': 'System Report',
        '/xero-clients': 'Xero Clients'
    };
    return pageNames[path] || path;
};
